<template>
  <div @close.stop>
    <div class="user-grid">
      <ngc-input-text
        label="First Name"
        placeholder="First name"
        data-cy="first-name-field"
        :value="firstName"
        required
        :max-length="50"
        @input="onFirstNameInput"
      />
      <ngc-input-text
        label="Last Name"
        placeholder="Last name"
        data-cy="last-name-field"
        :value="lastName"
        required
        :max-length="50"
        @input="onLastNameInput"
      />
      <ngc-input-text
        v-if="!edit"
        label="Email"
        placeholder="Enter email"
        data-cy="email-field"
        :value="email"
        required
        :error-text="emailError"
        @input="onEmailInput"
        @change="getExistingUser"
      />
      <ngc-input-text
        v-else
        label="Email"
        :value="email"
        :disabled="true"
      />
      <ngc-input-dropdown
        label="Account Type"
        data-cy="account-type-dropdown"
        :options.prop="roleTypeOptions"
        :value.prop="roleType"
        :disabled="edit ? !managesUsers : false"
        :placeholder="edit ? '' : 'Select a role for this user'"
        @change="onRoleTypeChange"
      />
      <ngc-input-dropdown
        label="Time Zone"
        data-cy="timezone-dropdown"
        :options.prop="timeZoneDropdownOptions"
        :value.prop="timeZone"
        :placeholder="edit ? '' : 'Select a preferred time zone for this user'"
        @change="onTimeZoneChange"
      />
      <env-select
        label="Environments"
        :value="enabledEnvironments"
        :items="environmentOptions"
        :disabled="true"
        placeholder="Inherited from account type"
      />
    </div>

    <div
      v-if="(!edit && !isSnAdminCompany) || (allowedOrganizations != null && allowedOrganizations.length > 0)"
      class="user-orgs"
    >
      <ngc-input-label class="contact-label">
        Organizations
      </ngc-input-label>
      <sr-organization-combobox
        v-model="organizations"
        class="orgs"
        data-cy="orgs-dropdown"
        :organizations="allowedOrganizations"
        :disabled="companiesDisabled"
        @input="handleChanges"
      />
    </div>

    <div
      v-if="edit"
      class="user-actions-links"
    >
      <ngc-button
        v-if="lastLoginAt == null"
        variant="base"
        data-cy="resend-invite"
        :disabled="loading"
        @click="resendInvite"
      >
        Resend Invite
      </ngc-button>
      <ngc-button
        variant="base"
        data-cy="password-reset"
        :disabled="passwordResetLoading"
        @click="showPasswordResetModal = true"
      >
        Require password reset on next login
      </ngc-button>
    </div>

    <ngc-modal
      :open="showPasswordResetModal"
      variant="decide"
      :title.prop="'Require password reset on next login'"
      data-cy="confirmation-modal"
      primary-action-text="Require Reset"
      :loading="passwordResetLoading"
      @primary-action-click="forcePasswordReset"
      @secondary-action-click="showPasswordResetModal = false"
      @close="showPasswordResetModal = false"
    >
      <span>{{ passwordResetUserName }} will be required to set a new password the next time they log in. This is a one-time action.</span>
    </ngc-modal>
  </div>
</template>

<script>

import srOrganizationCombobox from '../srui/srOrganizationCombobox'
import envSelect from '../srui/envSelect'
import snSigningRoomService, { TIMEZONES } from '../../services/snSigningRoomService'
import { mapGetters, mapMutations } from 'vuex'
import { user, editUser, addUser, updateUsersForcePasswordReset, companyName } from '../graphql/signing_room_queries.gql'
import { isEqual } from 'lodash'

export default {
  name: 'SrUserInfo',
  components: {
    srOrganizationCombobox,
    envSelect
  },
  props: {
    edit: {
      type: Boolean,
      required: true,
      default: false
    },
    user: {
      type: Object,
      required: false,
      default: () => {}
    },
    roleEnvironments: {
      type: Object,
      required: false,
      default: () => {}
    },
    roleTypes: {
      type: Array,
      required: false,
      default: () => []
    },
    orgs: {
      type: Array,
      required: false,
      default: () => []
    },
    userGuid: {
      type: String,
      default: '',
      required: false
    },
    companyGuid: {
      type: String,
      required: false,
      default: null
    }
  },
  data () {
    return {
      firstName: '',
      dbFirstName: '',
      lastName: '',
      dbLastName: '',
      timeZone: '',
      dbTimeZone: '',
      email: '',
      dbEmail: '',
      emailErrors: [],
      emailAlreadyInUse: false,
      dbEmailAlreadyInUse: false,
      roleType: '',
      dbRoleType: '',
      companyRoleTypes: [],
      timeZoneOptions: TIMEZONES.map(tz => tz.label),
      environmentOptions: ['Staging', 'Production'],
      enabledEnvironments: [],
      dbEnabledEnvironments: [],
      organizations: [],
      dbOrganizations: [],
      allowedOrganizations: [],
      validTimeZone: true,
      validRoleType: true,
      userInfoToChange: [],
      lastQueriedEmail: '',
      loading: false,
      saveBtnLoading: false,
      passwordResetLoading: false,
      showPasswordResetModal: false,
      companyName: ''
    }
  },
  computed: {
    ...mapGetters({
      loggedInUser: 'user',
      orgId: 'orgId'
    }),
    roleTypeOptions () {
      return (this.companyRoleTypes || []).map(role => ({ label: role, value: role }))
    },
    timeZoneDropdownOptions () {
      return this.timeZoneOptions.map(tz => ({ label: tz, value: tz }))
    },
    emailError () {
      if (this.emailErrors.length > 0) {
        return this.emailErrors[0]
      }
      if (!this.email) {
        return ''
      }
      return this.validateEmail(this.email) === true ? '' : 'Invalid email format'
    },
    hasUserInfoChanges () {
      return !!this.firstName &&
        !!this.lastName &&
        !!this.timeZone &&
        !!this.roleType &&
        !!this.email &&
        !this.emailAlreadyInUse &&
        this.enabledEnvironments.length > 0 &&
        (this.isSnAdminCompany || this.organizations?.length > 0) &&
        this.validTimeZone &&
        this.validRoleType &&
        this.validateEmail(this.email) === true &&
        this.limit50Chars(this.firstName) === true &&
        this.limit50Chars(this.lastName) === true &&
        !isEqual(this.dbValues, this.currentValues)
    },
    limit50Chars () {
      return (input) => input?.length >= 50 ? '50 character limit' : true
    },
    notEmpty () {
      return (input) => input?.length > 0 ? true : 'Required field'
    },
    validateEmail () {
      return (input) => input ? snSigningRoomService.validateEmail(input) ? true : 'Invalid email format' : false
    },
    managesUsers () {
      return snSigningRoomService.hasPermission(this.loggedInUser, 'manageUsers')
    },
    isEditingSelf () {
      return this.loggedInUser.guid === this.userGuid
    },
    companiesDisabled () {
      return !this.managesUsers || this.isEditingSelf
    },
    dbValues () {
      return { firstName: this.dbFirstName, lastName: this.dbLastName, timeZone: this.dbTimeZone, email: this.dbEmail, organizations: this.dbOrganizations.length ? JSON.stringify(this.dbOrganizations) : this.dbOrganizations, roleType: this.dbRoleType, enabledEnvironments: this.dbEnabledEnvironments }
    },
    currentValues () {
      return { firstName: this.firstName, lastName: this.lastName, timeZone: this.timeZone, email: this.email, organizations: this.organizations.length ? JSON.stringify(this.organizations) : this.organizations, roleType: this.roleType, enabledEnvironments: this.enabledEnvironments }
    },
    // Saved name, not the edited form values - the reset applies to the user as
    // they exist on the server, and unsaved edits shouldn't change the wording.
    passwordResetUserName () {
      const name = `${this.dbFirstName || ''} ${this.dbLastName || ''}`.trim()
      return name || 'This user'
    },
    lastLoginAt () { return this.user?.lastLoginAt },
    isSnAdminCompany () { return this.user?.company?.name === 'SnAdmin Company' || this.companyName === 'SnAdmin Company' }
  },
  watch: {
    user () {
      // update any user role info that changes when permissions are changed
      this.setEditUserInfo(this.user)
      this.environmentSettingOnChange()
    },
    roleEnvironments () {
      this.environmentSettingOnChange()
    },
    hasUserInfoChanges: {
      immediate: true,
      handler (val) {
        this.$emit('save-disabled', !val)
      }
    },
    saveBtnLoading (val) {
      this.$emit('saving', val)
    }
  },
  created () {
    if (!this.edit && this.roleTypes.length > 0) {
      this.roleType = this.roleTypes[0]
    }
    this.companyRoleTypes = this.roleTypes
    this.allowedOrganizations = this.orgs
    this.setEditUserInfo(this.user)
    this.environmentSettingOnChange()
    this.getCompanyName()
  },
  methods: {
    ...mapMutations(['setSuccessToast', 'setErrorToastType', 'resetAppTimeout']),
    onFirstNameInput (event) {
      this.firstName = event.detail.value
      this.handleChanges()
    },
    onLastNameInput (event) {
      this.lastName = event.detail.value
      this.handleChanges()
    },
    onEmailInput (event) {
      this.email = event.detail.value
      this.handleChanges()
    },
    onRoleTypeChange (event) {
      this.roleType = event.detail.value
      this.environmentSettingOnChange()
    },
    onTimeZoneChange (event) {
      this.timeZone = event.detail.value
      this.handleChanges()
    },
    setEditUserInfo (user) {
      if (this.edit) {
        this.firstName = user?.firstName || ''
        this.lastName = user?.lastName || ''
        this.timeZone = user?.timezone || ''
        this.roleType = user?.roleType || ''
        this.companyRoleTypes = user?.companyRoleTypes || []
        this.email = user?.email || ''
        this.allowedOrganizations = user?.allowedOrgsFormatted ? JSON.parse(user?.allowedOrgsFormatted) : []
        this.organizations = user?.selectedOrgsFormatted?.length ? JSON.parse(user?.selectedOrgsFormatted) : []
        this.dbFirstName = user?.firstName || ''
        this.dbLastName = user?.lastName || ''
        this.dbTimeZone = user?.timezone || ''
        this.dbEmail = user?.email || ''
        this.dbOrganizations = user?.selectedOrgsFormatted?.length ? JSON.parse(user?.selectedOrgsFormatted) : []
        this.dbRoleType = user?.roleType || ''
      }
    },
    async handleChanges () {
      if (this.validateEmail(this.email) === true && !this.edit) {
        await this.getExistingUser()
      }

      if (this.hasUserInfoChanges) {
        this.$emit('change', this.currentValues)
      } else {
        this.$emit('change', {})
      }
    },
    environmentSettingOnChange () {
      this.enabledEnvironments = this.roleEnvironments[this.roleType] || []
      this.dbEnabledEnvironments = this.roleEnvironments[this.roleType] || []
      this.handleChanges()
    },
    async getExistingUser () {
      if (this.edit) {
        return
      }
      if (this.email === this.lastQueriedEmail) {
        return
      }
      this.lastQueriedEmail = this.email

      const { data } = await this.$apollo.query({
        query: user,
        fetchPolicy: 'no-cache',
        variables: {
          email: this.email
        }
      })
      if (data.user) {
        this.emailErrors = ['Email already in use']
        this.emailAlreadyInUse = true
      } else {
        this.emailErrors = []
        this.emailAlreadyInUse = false
      }
    },
    getCompanyName () {
      this.$apollo.query({
        query: companyName,
        variables: {
          companyGuid: this.companyGuid
        },
        fetchPolicy: 'no-cache'
      }).then(({ data }) => {
        this.companyName = data.company.name
      })
    },
    resendInvite () {
      this.loading = true
      const { firstName, lastName, email, timeZone, stagingEnabled, productionEnabled, roleType } = this.user
      this.$apollo.mutate({
        mutation: addUser,
        variables: {
          // this.orgId is the admin's currently-viewed org (global nav context),
          // not necessarily any org this user is actually assigned to - use the
          // user's own selected orgs instead, same as handleSave() below.
          orgIds: this.organizations.map(o => o.guid),
          firstName,
          lastName,
          email,
          timeZone,
          stagingEnabled,
          productionEnabled,
          roleType,
          companyGuid: this.companyGuid,
          newInvite: false
        }
      }).then(({ data }) => {
        this.loading = false
        if (data.addUser.error) {
          this.setErrorToastType({ description: data.addUser.error || 'There was an error with the request' })
        } else {
          this.setSuccessToast({ description: 'Invitation sent' })
        }
        this.resetAppTimeout()
      }).catch(err => {
        this.loading = false
        this.setErrorToastType({ description: `There was an error with the request: ${err}` })
      })
    },
    handleSave () {
      this.saveBtnLoading = true
      let { firstName, lastName, timeZone, organizations, roleType, enabledEnvironments } = this.currentValues
      if (typeof organizations === 'string') {
        organizations = JSON.parse(organizations)
      }
      this.$apollo.mutate({
        mutation: editUser,
        variables: {
          guid: this.userGuid,
          orgIds: organizations.map(o => o.guid),
          firstName,
          lastName,
          timeZone,
          stagingEnabled: enabledEnvironments.includes('Staging'),
          productionEnabled: enabledEnvironments.includes('Production'),
          roleType
        }
      }).then(({ data }) => {
        if (data.editUser.response) {
          const reload = snSigningRoomService.checkForSelfReload([this.userGuid], this.loggedInUser)
          this.setSuccessToast({ description: `Updated user successfully${reload ? '. Applying...' : ''}` })
          this.$emit('save')
          if (!reload) {
            this.saveBtnLoading = false
          }
        } else {
          this.setErrorToastType({ description: data.editUser.error })
          this.saveBtnLoading = false
        }
      }).catch(err => {
        this.setErrorToastType({ description: err })
        this.saveBtnLoading = false
      })
    },
    forcePasswordReset () {
      this.passwordResetLoading = true
      this.$apollo
        .query({
          query: updateUsersForcePasswordReset,
          fetchPolicy: 'no-cache',
          variables: { guids: [this.userGuid] }
        })
        .then(({ data }) => {
          if (data.updateUsersForcePasswordReset?.response) {
            this.setSuccessToast({ description: 'Password reset has been initiated.' })
            this.showPasswordResetModal = false
          } else if (data.updateUsersForcePasswordReset?.error) {
            this.setErrorToastType({ description: data.updateUsersForcePasswordReset?.error })
          }
        }).catch((error) => {
          this.setErrorToastType({ description: error.message })
        }).finally(() => { this.passwordResetLoading = false })
    }
  }
}
</script>

<style scoped>

.user-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px 32px;
  margin-top: 24px;
}

.user-orgs {
  margin-top: 24px;
}

.contact-label {
  display: block;
  margin-bottom: 8px;
}

.user-actions-links {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

@media (max-width: 768px) {
  .user-grid {
    grid-template-columns: 1fr;
  }
}

</style>
